import React, { useState } from "react";
import { CourseFromGroupApi } from "api";
import { categoryFormGroupsApi, useCategoryFormGroups } from "api/category-form-groups";
import { PaziaButton, PaziaInlineLoader } from "components/atoms";
import { AdminResourceDeleteModal } from "components/molecules";
import { AddCategoryFormGroupModal } from "components/templates/AddCategoryFormGroupModal/AddCategoryFormGroupModal";
import { CategoryFormGroupSchema } from "components/templates/AddCategoryFormGroupModal/CategoryFormGroupForm";
import { useDeleteResourceActions } from "utils/store";
import { useToaster } from "utils";
import { CategoryFormGroupDetail } from "./CategoryFormGroupDetail";

export const CategoryFormGroups = ({ categoryId }: { categoryId: number }) => {
  const { isLoading, groups, refetch } = useCategoryFormGroups(categoryId);
  const { setDeleteResource } = useDeleteResourceActions();
  const { showSuccess, showError } = useToaster();
  const [activeGroup, setActiveGroup] = useState<CourseFromGroupApi | null>(null);
  const [modalGroup, setModalGroup] = useState<CourseFromGroupApi | null>(null);

  if (isLoading)
    return (
      <div className="flex w-full justify-center">
        <PaziaInlineLoader />
      </div>
    );

  const onGroupSubmit = async (formData: CategoryFormGroupSchema) => {
    try {
      if (formData.id) {
        await categoryFormGroupsApi.updateGroup(categoryId, formData.id, formData);
        showSuccess("Grupa została zaktualizowana.");
      } else {
        await categoryFormGroupsApi.addGroup(categoryId, formData);
        showSuccess("Grupa została dodana.");
      }
      await refetch();
      setModalGroup(null);
    } catch (e) {
      showError(formData.id ? "Nie udało się edytować grupy" : "Nie udało się dodać grupy");
    }
  };

  const activeGroupFresh = activeGroup
    ? (groups.find((g) => g.courseFormGroupId === activeGroup.courseFormGroupId) ?? null)
    : null;

  // Rendered inside CategoryEditPage's PaziaBox, so no card of its own
  return (
    <div>
      {!activeGroupFresh && (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full table-auto border-collapse border border-gray-200 dark:border-slate-700">
              <thead>
                <tr className="bg-gray-100 dark:bg-slate-900">
                  <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700">Nazwa</th>
                  <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700">Pola</th>
                  <th className="border border-gray-300 px-4 py-2 text-left dark:border-slate-700">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.courseFormGroupId} className="hover:bg-gray-100 dark:hover:bg-slate-700">
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">{group.name}</td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      {group.fields.length > 0 ? group.fields.map((field) => field.name).join(", ") : "Brak pól"}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 dark:border-slate-700">
                      <div className="flex gap-2">
                        <PaziaButton title="Pola" type="tertiary" onClick={() => setActiveGroup(group)} />
                        <PaziaButton title="Edytuj nazwę" type="tertiary" onClick={() => setModalGroup(group)} />
                        <PaziaButton
                          title="Usuń"
                          type="danger2"
                          onClick={() => setDeleteResource(group.courseFormGroupId)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-end">
            <PaziaButton title="Dodaj grupę" type="primary" onClick={() => setModalGroup({} as CourseFromGroupApi)} />
          </div>
        </>
      )}
      {activeGroupFresh && (
        <CategoryFormGroupDetail
          categoryId={categoryId}
          group={activeGroupFresh}
          allGroups={groups}
          changeActiveGroup={setActiveGroup}
          refreshGroups={refetch}
        />
      )}

      <AddCategoryFormGroupModal
        group={modalGroup}
        onCloseRequest={() => setModalGroup(null)}
        onSubmit={onGroupSubmit}
      />

      <AdminResourceDeleteModal
        title="Usuń grupę"
        description="Jesteś pewien że chcesz usunąć tę domyślną grupę kategorii? Zniknie z formularzy wszystkich kursów tej kategorii."
        onSuccessDelete={() => refetch()}
        deleteAction={async (id) => {
          await categoryFormGroupsApi.deleteGroup(categoryId, id);
        }}
      />
    </div>
  );
};
